import { type LocalDate, parseLocalDate, formatLocalDate } from '@taler/domain'
import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { firstValue } from '../internal/query-params.ts'
import {
  defaultReportRange,
  fittingBuckets,
  REPORT_BUCKETS,
  type ReportBucket,
  type ReportRange,
  steppedWindow,
} from '../internal/report-range.ts'

export interface ReportRangeQueryState {
  readonly range: ComputedRef<ReportRange>
  /** True when `from` falls after `until`; the page explains it instead of querying. */
  readonly isInverted: ComputedRef<boolean>
  /** True when no bucket splits the window within the limit; the page explains it instead of querying. */
  readonly isTooLong: ComputedRef<boolean>
  /** The buckets the window can be shown at, finest first. */
  readonly offeredBuckets: ComputedRef<readonly ReportBucket[]>
  setFrom(next: string): void
  setUntil(next: string): void
  setBucket(next: ReportBucket): void
  step(direction: -1 | 1): void
}

function parseDateInput(raw: string): LocalDate | undefined {
  try {
    return parseLocalDate(raw)
  } catch {
    return undefined
  }
}

function parseDate(raw: unknown, fallback: LocalDate): LocalDate {
  const candidate = firstValue(raw)
  return (candidate === undefined ? undefined : parseDateInput(candidate)) ?? fallback
}

function parseBucket(raw: unknown, fallback: ReportBucket): ReportBucket {
  const candidate = firstValue(raw)
  return (REPORT_BUCKETS as readonly string[]).includes(candidate ?? '')
    ? (candidate as ReportBucket)
    : fallback
}

/** The window and bucket live in the URL, so a shared link reopens on the same figures. */
export function useReportRangeQueryState(today: () => LocalDate): ReportRangeQueryState {
  const route = useRoute()
  const router = useRouter()

  const window = computed(() => {
    const fallback = defaultReportRange(today())
    return {
      from: parseDate(route.query['from'], fallback.from),
      until: parseDate(route.query['until'], fallback.until),
      preferred: parseBucket(route.query['bucket'], fallback.bucket),
    }
  })
  const offeredBuckets = computed(() => fittingBuckets(window.value))

  const range = computed<ReportRange>(() => {
    const { from, until, preferred } = window.value
    const bucket = offeredBuckets.value.includes(preferred) ? preferred : (offeredBuckets.value[0] ?? 'YEAR')
    return { from, until, bucket }
  })

  const write = (patch: Partial<Record<'from' | 'until' | 'bucket', string>>): void => {
    void router.replace({ query: { ...route.query, ...patch } })
  }

  const writeDate = (key: 'from' | 'until', next: string): void => {
    const parsed = parseDateInput(next)
    if (parsed !== undefined) write({ [key]: formatLocalDate(parsed) })
  }

  return {
    range,
    isInverted: computed(() => range.value.from.epochDay > range.value.until.epochDay),
    isTooLong: computed(() => offeredBuckets.value.length === 0),
    offeredBuckets,
    setFrom: (next) => writeDate('from', next),
    setUntil: (next) => writeDate('until', next),
    setBucket: (next) => write({ bucket: next }),
    step: (direction) => {
      const next = steppedWindow(range.value, direction)
      write({ from: formatLocalDate(next.from), until: formatLocalDate(next.until) })
    },
  }
}
